import { useCallback, useEffect, useRef, useState } from 'react';
import { Audio } from 'expo-av';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useExerciseSound } from './useExerciseSound';
import { Recording } from 'expo-av/build/Audio';
import { logError } from '@/utils/logHandling';
import { Alert, Linking, Platform } from 'react-native';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

// ? Time out for following up the record button click only for 4 secs no longer recording are allowed
let timeout: any = null;

export function useExerciseRecord(
  changer: unknown,
  timeoutSeconds: number,
  uniqueId: string,
) {
  const [isRecording, setIsRecording] = useState(false);
  const _recording = useRef<Recording | undefined>(undefined);
  const [uri, setUri] = useState('');
  const allowPlay = useRef(true);
  const [microphonePermission, setMicrophonePermission] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    (async () => {
      const { granted } = await Audio.getPermissionsAsync();
      setMicrophonePermission(granted);
    })();
  }, []);

  const { playSound, isPlaying, stopSound } = useExerciseSound(uri);
  const { showNotification } = useNotification();

  const showNoPermissionToast = () => {
    if (Platform.OS === 'web') {
      showNotification({
        type: 'error',
        title: 'Kein Zugriff auf das Mikrofon erteilt.',
      });
    } else {
      Alert.alert(
        'Kein Mikrofonzugriff',
        'Erlauben Sie den Zugriff auf das Mikrofon in den Einstellungen.',
        [
          {
            text: 'Einstellungen',
            onPress: Linking.openSettings,
          },
          {
            text: 'Abbrechen',
          },
        ],
      );
    }
  };

  const startRecording = useCallback(async () => {
    try {
      allowPlay.current = true;
      if (microphonePermission === null || !microphonePermission) {
        const { granted } = await Audio.requestPermissionsAsync();
        setMicrophonePermission(granted);
        if (!granted) {
          showNoPermissionToast();
          return;
        }
      }

      // Prevent multiple simultaneous recordings
      if (isRecording || _recording.current) {
        logError('Recording is already in progress');
        return;
      }

      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });

      // Add recording options to prevent hanging
      const recordingOptions = Audio.RecordingOptionsPresets.LOW_QUALITY;

      // Add timeout to prevent hanging
      const recordingPromise = Audio.Recording.createAsync(recordingOptions);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Recording creation timeout')), 8000),
      );
      const { recording } = (await Promise.race([
        recordingPromise,
        timeoutPromise,
      ])) as any;

      _recording.current = recording;
      setIsRecording(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes('permission') ||
        errorMessage.includes('Permission')
      ) {
        setMicrophonePermission(false);
        showNoPermissionToast();
      } else {
        logError('Failed to start recording', error);
      }
    }
  }, [microphonePermission]);

  const stopRecording = useCallback(
    async (notAbort: boolean) => {
      clearTimeout(timeout);
      setIsRecording(false);
      await _recording.current?.stopAndUnloadAsync?.();
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
      });
      if (!notAbort) {
        _recording.current = undefined;
        return;
      }
      const uri = _recording.current?.getURI();
      if (!uri) {
        throw new Error('No audio recording uri');
      }
      setUri(uri);

      // Cache the recording if uniqueId exists
      if (uniqueId) {
        try {
          await AsyncStorage.setItem(`@recording:${uniqueId}`, uri);
        } catch (error) {
          logError('Failed to cache recording', error);
        }
      }
      _recording.current = undefined;
    },
    [uniqueId],
  );

  const playRecording = () => {
    playSound();
  };

  const stopPlayingRecording = () => {
    stopSound();
  };

  useEffect(() => {
    if (!isRecording && uri && allowPlay.current) {
      playSound();
    }
  }, [uri]);

  useEffect(() => {
    if (uri) {
      setUri('');
    }
    if (isRecording) {
      stopRecording(false);
    }
  }, [changer]);

  useEffect(() => {
    if (isRecording) {
      timeout = setTimeout(() => {
        stopRecording(true);
      }, Number(timeoutSeconds));
    }
  }, [isRecording]);

  // Retrieve cached recording if uniqueId exists
  useEffect(() => {
    const getCachedRecording = async () => {
      if (uniqueId) {
        try {
          const cachedRecording = await AsyncStorage.getItem(
            `@recording:${uniqueId}`,
          );
          if (cachedRecording) {
            allowPlay.current = false;
            setUri(cachedRecording);
          }
        } catch (error) {
          logError('Failed to retrieve cached recording', error);
        }
      }
    };

    getCachedRecording();
  }, [uniqueId]);

  useEffect(() => {
    return () => {
      clearTimeout(timeout);
      _recording.current?.stopAndUnloadAsync?.();
      Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
      });
    };
  }, []);

  return {
    stopRecording,
    startRecording,
    isRecording,
    playRecording,
    stopPlayingRecording,
    isPlaying,
    uri,
  };
}
